import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import PresignedImage from '../common/PresignedImage';
import {
  downloadAttachment,
  splitFileNameParts,
  composeFileName,
  isDisplayableImage,
  isLinkAttachment,
  getLinkDisplayTitle,
  getFileExtensionLabel,
  formatFileSize,
  decodeLinkHref,
} from '../../utils/fileUtils';
import { useAttachmentPreview } from './useAttachmentPreview';
import ImageAnnotatorModal from './ImageAnnotatorModal';
import {
  isAttachmentLikedBy,
  likedByIds,
  toggleAttachmentLike,
  toggleLikedByUserIds,
  uploadImageAsAttachment,
  uploadAndReplaceAttachmentImage,
  withPreviousVersion,
} from '../../utils/attachmentMediaApi';
import { updateMessage, sendMessage } from '../../store/slices/chatSlice';
import { addNotification } from '../../store/slices/uiSlice';
import SaveEditedImageDialog from './SaveEditedImageDialog';
import { upsertRealmDocs } from '../../realm';
import { getPresignedFileUrl } from '../../services/fileUrlService';
import { isInlineFileSrc, resolveLocalPreviewUrl } from '../../services/offlineAttachmentStore';
import FileTypeIcon from '../common/FileTypeIcon';

const HeartIcon = ({ filled }) => (
  filled ? (
    <svg className="w-5 h-5 text-red-500" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
      />
    </svg>
  ) : (
    <svg className="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  )
);

const resolveDisplayUrl = async (fileKey, originalName) => {
  if (!fileKey) return null;
  if (isInlineFileSrc(fileKey)) return fileKey;
  const localUrl = await resolveLocalPreviewUrl(fileKey);
  if (localUrl) return localUrl;
  return getPresignedFileUrl(fileKey, originalName ? { originalName } : {});
};

const preloadImage = (url) =>
  new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });

/** Keeps both versions mounted and crossfades after both are decoded. */
const CompareImagePair = ({
  previousFileKey,
  previousFileName,
  editedFileKey,
  editedFileName,
  showingPrevious,
  className,
}) => {
  const [urls, setUrls] = useState({ previous: null, edited: null });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setUrls({ previous: null, edited: null });
    (async () => {
      try {
        const [previous, edited] = await Promise.all([
          resolveDisplayUrl(previousFileKey, previousFileName),
          resolveDisplayUrl(editedFileKey, editedFileName),
        ]);
        if (cancelled) return;
        await Promise.all([preloadImage(previous), preloadImage(edited)]);
        if (cancelled) return;
        setUrls({ previous, edited });
        setReady(true);
      } catch {
        if (!cancelled) setReady(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [previousFileKey, previousFileName, editedFileKey, editedFileName]);

  if (!ready || !urls.edited) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 min-h-[200px]">
        <div className="w-48 h-48 rounded-lg bg-gray-800 animate-pulse" />
        <p className="text-[11px] text-white/45">Preparing previous &amp; edited…</p>
      </div>
    );
  }

  return (
    <div className="relative grid place-items-center max-w-full max-h-[calc(100vh-14rem)] w-full">
      {urls.previous && (
        <img
          src={urls.previous}
          alt={previousFileName || 'Previous'}
          draggable={false}
          className={`${className} col-start-1 row-start-1 transition-opacity duration-500 ease-in-out ${
            showingPrevious ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        />
      )}
      <img
        src={urls.edited}
        alt={editedFileName || 'Edited'}
        draggable={false}
        className={`${className} col-start-1 row-start-1 transition-opacity duration-500 ease-in-out ${
          showingPrevious ? 'opacity-0 z-0' : 'opacity-100 z-10'
        }`}
      />
    </div>
  );
};

const getItemLabel = (item) => {
  if (!item) return '';
  if (isLinkAttachment(item)) {
    return getLinkDisplayTitle({
      url: item.fileUrl,
      title: item.fileName,
      customTitle: item.linkCustomTitle,
    });
  }
  return item.fileName || '';
};

const isImageItem = (item) => isDisplayableImage(item);

const ImageViewerModal = ({
  imageUrl,
  fileName,
  images,
  initialIndex = 0,
  onClose,
  onDownload,
  canRename = false,
  onRename,
  onEditedImage,
  onAttachmentMessageUpdated,
}) => {
  const dispatch = useDispatch();
  const currentUserId = useSelector((state) => state.auth?.user?.id);
  const buildGallery = useCallback(() => (
    images && images.length > 0
      ? images
      : imageUrl
        ? [{ fileUrl: imageUrl, fileName: fileName || 'image' }]
        : []
  ), [images, imageUrl, fileName]);

  const [gallery, setGallery] = useState(buildGallery);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [annotating, setAnnotating] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [pendingEditedFile, setPendingEditedFile] = useState(null);
  const [compareTab, setCompareTab] = useState('edited'); // 'previous' | 'edited'
  const thumbStripRef = useRef(null);

  useEffect(() => {
    const nextGallery = buildGallery();
    setGallery(nextGallery);
    const safeIndex = Math.min(Math.max(initialIndex, 0), Math.max(nextGallery.length - 1, 0));
    setCurrentIndex(safeIndex);
    setIsEditing(false);
    setEditName('');
    setCompareTab('edited');
  }, [buildGallery, initialIndex, images, imageUrl]);

  const current = gallery[currentIndex];
  const hasMultiple = gallery.length > 1;

  useEffect(() => {
    setCompareTab('edited');
  }, [currentIndex, current?.fileUrl, current?.previousFileUrl]);

  useEffect(() => {
    setCurrentIndex((i) => Math.min(i, Math.max(gallery.length - 1, 0)));
    setIsEditing(false);
  }, [gallery.length]);

  const goPrev = useCallback(() => {
    setIsEditing(false);
    setCurrentIndex((i) => (i > 0 ? i - 1 : gallery.length - 1));
  }, [gallery.length]);

  const goNext = useCallback(() => {
    setIsEditing(false);
    setCurrentIndex((i) => (i < gallery.length - 1 ? i + 1 : 0));
  }, [gallery.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (annotating) return;
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasMultiple) {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight' && hasMultiple) {
        e.preventDefault();
        goNext();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, hasMultiple, goPrev, goNext, annotating]);

  useEffect(() => {
    const strip = thumbStripRef.current;
    if (!strip) return;
    const active = strip.querySelector('[data-active="true"]');
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [currentIndex]);

  const handleDownload = async () => {
    if (!current) return;
    try {
      if (onDownload) {
        onDownload(current);
      } else {
        await downloadAttachment(current);
      }
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const startRename = () => {
    if (!current) return;
    if (isLinkAttachment(current)) {
      setEditName(getItemLabel(current));
    } else if (current.fileName) {
      setEditName(splitFileNameParts(current.fileName).baseName);
    }
    setIsEditing(true);
  };

  const cancelRename = () => {
    setIsEditing(false);
    setEditName('');
  };

  const saveRename = async () => {
    if (!current || !onRename) {
      cancelRename();
      return;
    }
    const trimmed = editName.trim();
    const isLink = isLinkAttachment(current);
    if (!trimmed) {
      cancelRename();
      return;
    }
    if (isLink) {
      if (trimmed === getItemLabel(current)) {
        cancelRename();
        return;
      }
    } else {
      const { baseName: currentBase, extension } = splitFileNameParts(current.fileName);
      if (trimmed === currentBase) {
        cancelRename();
        return;
      }
    }
    setIsSaving(true);
    try {
      await onRename(current, trimmed);
      if (isLink) {
        setGallery((prev) =>
          prev.map((item, idx) => (
            idx === currentIndex
              ? { ...item, linkCustomTitle: trimmed, fileName: item.fileName || trimmed }
              : item
          ))
        );
      } else {
        const { extension } = splitFileNameParts(current.fileName);
        const nextFileName = composeFileName(trimmed, extension);
        setGallery((prev) =>
          prev.map((item, idx) => (
            idx === currentIndex ? { ...item, fileName: nextFileName } : item
          ))
        );
      }
      cancelRename();
    } catch {
      // Parent handles error notification
    } finally {
      setIsSaving(false);
    }
  };

  const applyMessageUpdate = (message) => {
    if (!message?.id) return;
    const channelId = message.channelId || current?.channelId;
    if (channelId) {
      dispatch(updateMessage({
        channelId,
        message: message.channelId ? message : { ...message, channelId },
      }));
    }
    upsertRealmDocs('Message', [message]);
    onAttachmentMessageUpdated?.(message);
    const nextAtt = (message.attachments || []).find(
      (item) =>
        (current?.id && item.id === current.id)
        || (current?.fileUrl && item.fileUrl === current.fileUrl)
        || (current?.previousFileUrl && item.previousFileUrl === current.previousFileUrl)
    );
    if (!nextAtt) return;
    setGallery((prev) =>
      prev.map((item, idx) => (
        idx === currentIndex
          ? {
              ...item,
              ...nextAtt,
              messageId: item.messageId || message.id,
              channelId: item.channelId || message.channelId,
              // Drop local File blob after server replace so reload uses the new URL.
              file: undefined,
            }
          : item
      ))
    );
  };

  const handleToggleLike = async () => {
    if (!current || isLinkAttachment(current)) return;
    const messageId = current.messageId;
    const attachmentId = current.id;
    if (currentUserId) {
      setGallery((prev) =>
        prev.map((item, idx) => (
          idx === currentIndex
            ? { ...item, likedByUserIds: toggleLikedByUserIds(item.likedByUserIds, currentUserId) }
            : item
        ))
      );
    }
    if (!messageId || !attachmentId) {
      return;
    }
    try {
      const message = await toggleAttachmentLike(messageId, attachmentId);
      applyMessageUpdate(message);
    } catch (error) {
      if (currentUserId) {
        setGallery((prev) =>
          prev.map((item, idx) => (
            idx === currentIndex
              ? { ...item, likedByUserIds: toggleLikedByUserIds(item.likedByUserIds, currentUserId) }
              : item
          ))
        );
      }
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data || error?.message || 'Could not like this file',
      }));
    }
  };

  const channelMessages = useSelector((state) => (
    current?.channelId ? state.chat.messages?.[current.channelId] : null
  ));
  const imageOwnerId = current?.senderId
    || (Array.isArray(channelMessages)
      ? channelMessages.find((message) => String(message.id) === String(current?.messageId))?.senderId
      : null);
  const canOverrideImage = !imageOwnerId || String(imageOwnerId) === String(currentUserId);

  const postEditedImageAsNew = async (file) => {
    const channelId = current.channelId;
    if (!channelId) {
      throw new Error('Could not post edited image to this chat');
    }
    const uploaded = await uploadImageAsAttachment(file, current);
    await dispatch(sendMessage({
      content: '',
      channelId,
      senderId: currentUserId,
      attachments: [uploaded],
      replyToMessageId: current.replyToMessageId || current.messageId || undefined,
    })).unwrap();
    setAnnotating(false);
    setPendingEditedFile(null);
    onClose();
    dispatch(addNotification({
      type: 'success',
      message: 'Edited image posted to chat',
    }));
  };

  const overrideEditedImage = async (file) => {
    const message = await uploadAndReplaceAttachmentImage({
      messageId: current.messageId,
      attachmentId: current.id,
      file,
      previousAttachment: current,
    });
    applyMessageUpdate(message);
    setCompareTab('edited');
    setAnnotating(false);
    setPendingEditedFile(null);
    dispatch(addNotification({
      type: 'success',
      message: 'Edited image saved',
    }));
  };

  const handleSaveEditedImage = async (file) => {
    if (!current) return;
    if (onEditedImage) {
      setSavingEdit(true);
      try {
        await onEditedImage(current, file, currentIndex);
        const previewUrl = URL.createObjectURL(file);
        setGallery((prev) =>
          prev.map((item, idx) => (
            idx === currentIndex
              ? withPreviousVersion(item, file, previewUrl)
              : item
          ))
        );
        setCompareTab('edited');
        setAnnotating(false);
      } catch (error) {
        dispatch(addNotification({
          type: 'error',
          message: error?.response?.data || error?.message || 'Could not save edited image',
        }));
      } finally {
        setSavingEdit(false);
      }
      return;
    }

    if (current.messageId && current.id) {
      setPendingEditedFile(file);
      return;
    }

    setSavingEdit(true);
    try {
      await postEditedImageAsNew(file);
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: error?.response?.data || error?.message || 'Could not save edited image',
      }));
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmEditedImageSave = async (mode) => {
    const file = pendingEditedFile;
    if (!file || !current) return;
    setSavingEdit(true);
    try {
      if (mode === 'override') {
        await overrideEditedImage(file);
      } else {
        await postEditedImageAsNew(file);
      }
    } catch (error) {
      const raw = error?.response?.data;
      const message = typeof raw === 'string'
        ? raw
        : (raw?.error || error?.message || 'Could not save edited image');
      dispatch(addNotification({ type: 'error', message }));
    } finally {
      setSavingEdit(false);
    }
  };

  const isLink = current ? isLinkAttachment(current) : false;
  const linkHref = current && isLink ? decodeLinkHref(current.fileUrl) : '';
  const fileExtension = current && !isLink ? splitFileNameParts(current.fileName).extension : '';
  const displayLabel = getItemLabel(current);
  const { kind: sliderPreviewKind, pdfUrl, textContent, textLoading, textError } = useAttachmentPreview(current);
  const liked = isAttachmentLikedBy(current, currentUserId);
  const likeCount = likedByIds(current).length;
  const canLike = Boolean(current && !isLink);
  const canEditImage = sliderPreviewKind === 'image';
  const hasPreviousVersion = Boolean(
    sliderPreviewKind === 'image' && current?.previousFileUrl && current.previousFileUrl !== current.fileUrl
  );
  const showingPrevious = hasPreviousVersion && compareTab === 'previous';
  const imageClassName =
    'max-w-full max-h-[calc(100vh-14rem)] object-contain select-none rounded-lg';
  const imageLoadingClassName =
    'min-h-[200px] min-w-[200px] max-w-full max-h-[calc(100vh-14rem)] rounded-lg bg-gray-800 animate-pulse';

  const handleCompareTab = (tab) => {
    setCompareTab(tab);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!current) return null;

  return ReactDOM.createPortal(
    <>
    <div
      className="fixed inset-0 z-[1100] flex flex-col bg-black/95"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Resource viewer"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 shrink-0 border-b border-white/10">
        <div className="min-w-0 flex-1 pr-4">
          {isEditing ? (
            <div className="flex items-center gap-1.5 max-w-md">
              <div className="flex flex-1 min-w-0 items-center border border-white/30 rounded overflow-hidden bg-white/10">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 min-w-0 px-2 py-1 text-sm text-white bg-transparent focus:outline-none"
                  autoFocus
                  disabled={isSaving}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveRename();
                    if (e.key === 'Escape') cancelRename();
                  }}
                />
                {fileExtension && !isLink && (
                  <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white/70 bg-white/10 border-l border-white/20 select-none">
                    {fileExtension.replace(/^\./, '')}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={saveRename}
                disabled={isSaving}
                className="p-1.5 text-green-400 hover:text-green-300 disabled:opacity-50"
                title="Save name"
                aria-label="Save file name"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={cancelRename}
                disabled={isSaving}
                className="p-1.5 text-red-400 hover:text-red-300 disabled:opacity-50"
                title="Cancel"
                aria-label="Cancel rename"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <p className="text-white text-sm font-medium truncate">{displayLabel}</p>
          )}
          {hasMultiple && !isEditing && (
            <p className="text-white/50 text-xs mt-0.5">
              {currentIndex + 1} of {gallery.length}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {canLike && (
            <button
              type="button"
              onClick={handleToggleLike}
              className={`p-2 rounded-full transition-colors ${
                liked ? 'bg-red-500/15 hover:bg-red-500/25' : 'hover:bg-white/10'
              }`}
              aria-label={liked ? 'Unlike' : 'Like'}
              title={liked ? 'Unlike' : 'Like'}
            >
              <span className="inline-flex items-center gap-1">
                <HeartIcon filled={liked} />
                {likeCount > 0 && (
                  <span className={`text-[11px] font-semibold tabular-nums ${liked ? 'text-red-500' : 'text-white/80'}`}>{likeCount}</span>
                )}
              </span>
            </button>
          )}
          {canEditImage && (
            <button
              type="button"
              onClick={() => setAnnotating(true)}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Edit image"
              title="Edit image"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
          )}
          {canRename && onRename && !isEditing && (
            <button
              type="button"
              onClick={startRename}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label={`Rename ${displayLabel}`}
              title="Rename"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
          {!isLink && (
            <button
              type="button"
              onClick={handleDownload}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Download file"
              title="Download"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>
          )}
          {isLink && linkHref && (
            <a
              href={linkHref}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Open link in new tab"
              title="Open link"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Large preview with prev/next */}
      <div
        className="relative flex-1 flex items-center justify-center min-h-0 px-4 sm:px-16 py-4"
        onClick={(e) => e.stopPropagation()}
      >
        {hasPreviousVersion && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 p-1 rounded-full bg-black/60 border border-white/15">
            <button
              type="button"
              onClick={() => handleCompareTab('previous')}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                compareTab === 'previous'
                  ? 'bg-white text-gray-900'
                  : 'text-white/75 hover:text-white'
              }`}
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => handleCompareTab('edited')}
              className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                compareTab === 'edited'
                  ? 'bg-white text-gray-900'
                  : 'text-white/75 hover:text-white'
              }`}
            >
              Edited
            </button>
          </div>
        )}
        {hasMultiple && (
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 p-3 text-white bg-black/50 hover:bg-black/70 rounded-full border border-white/20 shadow-lg transition-colors"
            aria-label="Previous item"
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {sliderPreviewKind === 'image' && hasPreviousVersion && (
          <CompareImagePair
            previousFileKey={current.previousFileUrl}
            previousFileName={current.previousFileName || current.fileName}
            editedFileKey={current.fileUrl}
            editedFileName={current.fileName}
            showingPrevious={showingPrevious}
            className={imageClassName}
          />
        )}

        {sliderPreviewKind === 'image' && !hasPreviousVersion && (
          <PresignedImage
            key={current.fileUrl || currentIndex}
            fileKey={current.fileUrl}
            alt={displayLabel}
            originalName={current.fileName}
            className={imageClassName}
            loadingClassName={imageLoadingClassName}
          />
        )}

        {sliderPreviewKind === 'pdf' && (
          <div className="w-full max-w-4xl h-full max-h-[calc(100vh-14rem)] rounded-lg overflow-hidden bg-white shadow-2xl">
            {pdfUrl ? (
              <iframe
                title={current.fileName}
                src={`${pdfUrl}#page=1&toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
                className="w-full h-full border-0 bg-white"
              />
            ) : (
              <div className="w-full h-full min-h-[280px] flex items-center justify-center bg-gray-800 animate-pulse">
                <span className="text-sm text-white/50">Loading PDF…</span>
              </div>
            )}
          </div>
        )}

        {(sliderPreviewKind === 'text' || sliderPreviewKind === 'docx') && (
          <div className="w-full max-w-4xl max-h-[calc(100vh-14rem)] rounded-lg border border-white/20 bg-gray-900/90 shadow-2xl flex flex-col overflow-hidden">
            {sliderPreviewKind === 'docx' && !textLoading && !textError && (
              <p className="shrink-0 px-4 pt-3 text-[11px] text-white/45">
                Text preview of Word document — layout and images are not shown
              </p>
            )}
            <div className="flex-1 min-h-0 overflow-auto p-4 sm:p-6">
              {textLoading && (
                <div className="flex items-center justify-center min-h-[200px] text-white/50 text-sm">
                  {sliderPreviewKind === 'docx' ? 'Loading document…' : 'Loading text…'}
                </div>
              )}
              {!textLoading && textError && (
                <div className="flex items-center justify-center min-h-[200px] text-white/50 text-sm text-center px-4">
                  {textError}
                </div>
              )}
              {!textLoading && !textError && textContent != null && (
                <pre className="text-sm text-white/90 whitespace-pre-wrap break-words font-mono leading-relaxed">
                  {textContent}
                </pre>
              )}
            </div>
          </div>
        )}

        {sliderPreviewKind === 'none' && (
          <div className="max-w-lg w-full rounded-2xl border border-white/20 bg-white/5 backdrop-blur-sm px-8 py-10 text-center shadow-2xl">
            {isLink ? (
              <svg className="w-16 h-16 mx-auto text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            ) : (
              <FileTypeIcon
                attachment={current}
                size="lg"
                className="mx-auto !w-16 !h-16 !text-sm"
              />
            )}
            <p className="mt-5 text-white text-lg font-semibold break-words leading-snug">
              {displayLabel}
            </p>
            {!isLink && getFileExtensionLabel(current.fileName) && (
              <span className="inline-block mt-3 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide bg-white/10 text-white/80 rounded-md">
                {getFileExtensionLabel(current.fileName)}
              </span>
            )}
            {isLink && linkHref && (
              <a
                href={linkHref}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 text-sm text-primary-300 break-all line-clamp-2 hover:text-primary-200 hover:underline"
              >
                {current.linkSiteName || current.linkDomain || linkHref}
              </a>
            )}
            {!isLink && current.fileSize != null && (
              <p className="mt-3 text-sm text-white/50">
                {formatFileSize(current.fileSize)}
              </p>
            )}
            {!isLink && (
              <p className="mt-4 text-xs text-white/40">
                Preview not available for this file type. Use Download.
              </p>
            )}
          </div>
        )}

        {hasMultiple && (
          <button
            type="button"
            onClick={goNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 p-3 text-white bg-black/50 hover:bg-black/70 rounded-full border border-white/20 shadow-lg transition-colors"
            aria-label="Next item"
          >
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Thumbnail strip */}
      {hasMultiple && (
        <div
          className="shrink-0 border-t border-white/10 bg-black/40 px-4 py-3"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-white/50 text-xs mb-2 px-1">All items</p>
          <div
            ref={thumbStripRef}
            className="flex gap-2 overflow-x-auto py-4 px-1 scrollbar-thin scrollbar-thumb-white/20"
          >
            {gallery.map((item, idx) => {
              const isActive = idx === currentIndex;
              const label = getItemLabel(item);
              return (
                <button
                  key={item.id || item.fileUrl || idx}
                  type="button"
                  data-active={isActive}
                  onClick={() => {
                    setIsEditing(false);
                    setCurrentIndex(idx);
                  }}
                  className={`relative flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden transition-all ${
                    isActive
                      ? 'ring-2 ring-white ring-offset-2 ring-offset-black/80 opacity-100'
                      : 'opacity-60 hover:opacity-90'
                  }`}
                  aria-label={`View item ${idx + 1}: ${label}`}
                  aria-current={isActive}
                >
                  {isImageItem(item) ? (
                    <PresignedImage
                      fileKey={item.fileUrl}
                      alt={label}
                      originalName={item.fileName}
                      className="w-full h-full object-cover"
                      loadingClassName="w-full h-full bg-gray-700 animate-pulse"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-800 px-1">
                      {isLinkAttachment(item) ? (
                        <svg className="w-6 h-6 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      ) : (
                        <FileTypeIcon
                          attachment={item}
                          size="sm"
                          className="!w-10 !h-10"
                        />
                      )}
                      <span className="mt-1 text-[8px] font-bold uppercase text-white/50 truncate w-full text-center">
                        {isLinkAttachment(item) ? 'LINK' : (getFileExtensionLabel(item.fileName) || 'FILE')}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
    {annotating && current && (
      <ImageAnnotatorModal
        attachment={current}
        onClose={() => {
          if (savingEdit) return;
          setPendingEditedFile(null);
          setAnnotating(false);
        }}
        onSave={handleSaveEditedImage}
        saving={savingEdit && !pendingEditedFile}
      />
    )}
    {pendingEditedFile && (
      <SaveEditedImageDialog
        canOverride={canOverrideImage}
        saving={savingEdit}
        onOverride={() => confirmEditedImageSave('override')}
        onSaveNew={() => confirmEditedImageSave('new')}
        onCancel={() => {
          if (savingEdit) return;
          setPendingEditedFile(null);
        }}
      />
    )}
    </>,
    document.body
  );
};

export default ImageViewerModal;
